import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useDeleteQrByBatchCodeMutation, useFetchDeletedQrListMutation } from "../../../features/tenant/qrCode/qrCodeApi";
import { toast } from "react-hot-toast";

const DeleteQr = () => {
    const [batchStartCode, setBatchStartCode] = useState("");
    const [batchEndCode, setBatchEndCode] = useState("");
    const [reason, setReason] = useState("");
    const [deletedQrList, setDeletedQrList] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);

    const reasons = [
        "Damaged Product",
        "QA Rejected",
        "Incorrect Label Applied",
        "Batch / SKU Mismatch",
        "QC Sample"
    ];

    const [
        deleteQrByBatchCode,
        {
            data: deleteQrData,
            isLoading: deleteQrLoading,
            error: deleteQrError,
        },
    ] = useDeleteQrByBatchCodeMutation();

    const [
        fetchDeletedQrList,
        {
            data: deletedQrListData,
            isLoading: deletedQrListLoading,
            error: deletedQrListError,
        },
    ] = useFetchDeletedQrListMutation();

    const columns = [
        {
            name: "SNo",
            cell: (row, idx) => offset + Number(idx + 1),
            sortable: true,
            width: "80px",
        },
        {
            name: "Batch Running Code",
            cell: (row) => row.batch_running_code,
            sortable: true,
            width: "180px",
        },
        {
            name: "Batch Code",
            cell: (row) => row.batch_code,
            sortable: true,
            width: "130px",
        },
        {
            name: "Product Code",
            cell: (row) => row.product_code,
            sortable: true,
            width: "130px",
        },
        {
            name: "Unique Code",
            cell: (row) => row.unique_code,
            sortable: true,
            width: "150px",
        },
        {
            name: "Scratch Code",
            cell: (row) => row.scratch_code,
            sortable: true,
            width: "130px",
        },
        {
            name: "QR Type",
            cell: (row) => (row.qr_type === "1" ? "Standard" : "Enterprise"),
            sortable: true,
            width: "100px",
        },
        {
            name: "MRP",
            cell: (row) => row.mrp,
            sortable: true,
            width: "100px",
        },
        {
            name: "Created At",
            cell: (row) => new Date(row.created_at).toLocaleString(),
            sortable: true,
            width: "180px",
        },
        {
            name: "Deleted By",
            cell: (row) => row.created_by_name || "N/A",
            sortable: true,
            width: "150px",
        },
        {
            name: "Reason",
            cell: (row) => row.reason || "N/A",
            sortable: true,
            width: "200px",
        },
    ];

    const handleSubmit = () => {
        if (!batchStartCode || !batchEndCode || !reason) {
            toast.error("Please enter Batch Codes and select a Reason");
            return;
        }

        if (batchStartCode > batchEndCode) {
            toast.error("Batch Start Code must be less than or equal to Batch End Code");
            return;
        }

        deleteQrByBatchCode({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: {
                batch_start_code: batchStartCode,
                batch_end_code: batchEndCode,
                reason: reason,
            },
        });
    };

    const fetchDeletedQrs = () => {
        fetchDeletedQrList({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: {
                limit,
                offset,
            },
        });
    };

    useEffect(() => {
        fetchDeletedQrs();
    }, [limit, offset]);

    useEffect(() => {
        if (deleteQrData && deleteQrData.success) {
            toast.success(deleteQrData.message || "QR codes deleted successfully");
            setBatchStartCode("");
            setBatchEndCode("");
            setReason("");
            // Refresh the deleted QR list
            fetchDeletedQrs();
        }
    }, [deleteQrData]);

    useEffect(() => {
        if (deleteQrError) {
            toast.error(deleteQrError?.data?.message || "Error deleting QR codes");
        }
    }, [deleteQrError]);

    useEffect(() => {
        if (deletedQrListData && deletedQrListData.success) {
            const list = deletedQrListData.body?.data || [];
            setDeletedQrList(list);
            setTotalRows(deletedQrListData.body?.total || 0);
        }
    }, [deletedQrListData]);

    useEffect(() => {
        if (deletedQrListError) {
            toast.error("Error fetching deleted QR list");
        }
    }, [deletedQrListError]);

    const handlePageChange = (pageNumber) => {
        const newOffset = (pageNumber - 1) * limit;
        setOffset(newOffset);
    };

    const handlePerRowsChange = (newPerPage) => {
        setLimit(newPerPage);
        setOffset(0);
    };

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                        Delete QR Codes
                        <LinksComponent />
                    </div>

                    <div className="p-3">
                        <div className="bg-white shadow rounded-lg mb-4">
                            <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
                                <h3 className="text-lg font-semibold mb-3">Delete QR by Batch Code Range</h3>
                                <div className="flex space-x-4 items-end">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                            Batch Start Code
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter Batch Start Code"
                                            value={batchStartCode}
                                            onChange={(e) => setBatchStartCode(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                            Batch End Code
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter Batch End Code"
                                            value={batchEndCode}
                                            onChange={(e) => setBatchEndCode(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                            Reason
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                        >
                                            <option value="">Select Reason</option>
                                            {reasons.map((r, idx) => (
                                                <option key={idx} value={r}>
                                                    {r}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <button
                                            className="btn btn-outline-primary px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                            onClick={handleSubmit}
                                            disabled={deleteQrLoading}
                                        >
                                            {deleteQrLoading ? "Deleting..." : "Delete QR Codes"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white shadow rounded-lg">
                            <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
                                <h3 className="text-lg font-semibold">Deleted QR List</h3>
                            </div>
                            <DataTableComponent
                                keyField={(row) => row.id}
                                columns={columns}
                                data={deletedQrList}
                                loading={deletedQrListLoading}
                                totalRows={totalRows}
                                handlePerRowsChange={handlePerRowsChange}
                                handlePageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DeleteQr;
